import { api } from './axios'

// Global deduplication cache to prevent duplicate API calls
const pendingRequests = new Map()
const requestCache = new Map()
const CACHE_DURATION = 5000 // 5 seconds cache

// Create a unique key for the request
const getRequestKey = (method, url, data) => {
  // Normalize URL to handle query parameter ordering
  let normalizedUrl = url
  try {
    // Extract path and query string
    const [path, queryString] = url.split('?')
    
    if (queryString) {
      // Parse and sort query parameters for consistent keys
      const params = new URLSearchParams(queryString)
      const sortedParams = Array.from(params.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, value]) => `${key}=${value}`)
        .join('&')
      normalizedUrl = path + (sortedParams ? `?${sortedParams}` : '')
    }
  } catch {
    // If parsing fails, use original
    normalizedUrl = url
  }
  
  const dataStr = data ? JSON.stringify(data) : ''
  return `${method.toUpperCase()}:${normalizedUrl}:${dataStr}`
}

// Clean up old cache entries
const cleanupCache = () => {
  const now = Date.now()
  for (const [key, { timestamp }] of requestCache.entries()) {
    if (now - timestamp > CACHE_DURATION) {
      requestCache.delete(key)
    }
  }
}

export const apiRequest = async (method, url, data, options = {}) => {
  const { skipCache = false, ...requestOptions } = options

  const requestKey = getRequestKey(method, url, data)

  // Check if same request is already in progress
  if (pendingRequests.has(requestKey)) {
    // Return the existing promise
    return pendingRequests.get(requestKey)
  }

  // Check if we have a recent cached response
  if (!skipCache) {
    cleanupCache()
    if (requestCache.has(requestKey)) {
      const cached = requestCache.get(requestKey)
      if (Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached.response
      }
    }
  }

  // Create the request promise
  const requestPromise = (async () => {
    try {
      const response = await api({
        method,
        url,
        data,
        ...requestOptions,
      })

      // Cache successful responses (skip for highly dynamic lists)
      if (response.status === 200 && !skipCache) {
        requestCache.set(requestKey, {
          response,
          timestamp: Date.now()
        })
      }
      
      return response
    } catch (error) {
      throw error
    } finally {
      // Remove from pending requests after completion
      pendingRequests.delete(requestKey)
    }
  })()

  // Store the promise
  pendingRequests.set(requestKey, requestPromise)

  return requestPromise
}
